/**
 * Analytics Service
 * Centralized data fetching and aggregation for dashboard
 *
 * Structure:
 *   - Shared raw fetchers (fetchSessionDocsInRange / fetchPaymentDocsInRange /
 *     fetchBoothDocs) read each collection ONCE per date range per 5-min TTL,
 *     de-duplicating the identical queries the dashboard widgets used to run
 *     independently. Raw snapshots are memoized in memory only (they are not
 *     JSON-serializable), while each widget's computed result keeps the
 *     original two-tier (memory + localStorage) cache.
 *   - Pure compute* functions turn raw docs into each widget's result. They
 *     contain the exact aggregation logic the widgets used inline before and
 *     are exported for unit testing.
 */

import {
  collection,
  getDocs,
  getCountFromServer,
  getAggregateFromServer,
  count,
  sum,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import * as Sentry from '@sentry/react';
import { isInternalSession, INTERNAL_ROLES } from '../utils/internalSessions';
import {
  startOfDay,
  endOfDay,
  subDays,
  eachDayOfInterval,
  eachWeekOfInterval,
  format,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
  eachHourOfInterval,
  eachMonthOfInterval
} from 'date-fns';

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const cache = new Map();
const STORAGE_PREFIX = 'ss_analytics_';

// Safety caps so a runaway date range can never drain the read quota.
// Date-range queries cap at 5000; "all time" queries cap at 10000.
// At our current scale (~2K sessions/month) these are well above realistic
// workloads — if they ever hit, the page should add a "showing most recent N"
// hint and prompt the user to narrow the range.
const SAFETY_LIMIT = 5000;
const ALL_TIME_LIMIT = 10000;

// Truncation smoke alarm: if any query returns exactly its cap, we've
// silently dropped data. Warn once per label per session — to console
// and Sentry — so we know to raise the cap or paginate before numbers
// start underreporting.
const truncationWarned = new Set();
const checkTruncation = (snap, limitValue, label) => {
  if (!snap || typeof snap.size !== 'number') return;
  if (snap.size < limitValue) return;
  if (truncationWarned.has(label)) return;
  truncationWarned.add(label);
  const msg = `[analytics] Query "${label}" returned exactly ${limitValue} docs — likely truncated. Raise the cap or paginate.`;
  console.warn(msg);
  try {
    Sentry.captureMessage(msg, {
      level: 'warning',
      tags: { service: 'analytics', query_label: label, limit: String(limitValue) }
    });
  } catch (_e) { /* noop */ }
};

/**
 * Whether a stripe_payments doc counts toward revenue.
 * Excludes refunded payments and any payment with an active or lost dispute.
 * Only an explicit `dispute_outcome === 'won'` returns the funds, so we keep it.
 */
export const isCountedAsRevenue = (data) => {
  if (!data || data.status !== 'completed') return false;
  const ds = data.dispute_status;
  if (!ds) return true;
  if (ds === 'won') return true;
  return false;
};

/**
 * Resolve a dateRange value into { start, end, previousStart, previousEnd, isAllTime, cacheKey }
 *
 * Supported values:
 *   'week'      - This week (Sun–Sat), previous = last week
 *   'month'     - This month, previous = last month
 *   '2026-02'   - Specific month, previous = month before
 *   null        - All time, no previous period
 */
// Exported so the org revenue card can ask the server for the SAME window
// the charts are showing. Scaling a monthly figure to fit a weekly selector
// would be an approximation; the server accrues any window exactly.
export const resolveDateRange = (dateRange) => {
  const now = new Date();

  if (dateRange === null) {
    return {
      start: new Date(0),
      end: now,
      previousStart: new Date(0),
      previousEnd: new Date(0),
      isAllTime: true,
      cacheKey: 'all'
    };
  }

  if (dateRange === 'today') {
    const dayStart = startOfDay(now);
    const yesterday = subDays(now, 1);
    const prevDayStart = startOfDay(yesterday);
    const prevDayEnd = endOfDay(yesterday);
    return {
      start: dayStart,
      end: now,
      previousStart: prevDayStart,
      previousEnd: prevDayEnd,
      isAllTime: false,
      cacheKey: 'today'
    };
  }

  if (dateRange === 'week') {
    // Week-to-date vs same-weekday-and-time last week — same MTD logic as
    // 'month', so the % change reflects "vs this point last week" instead
    // of "vs all of last week."
    const weekOpts = { weekStartsOn: 1 }; // Monday
    const weekStart = startOfWeek(now, weekOpts);
    const prevWeekStart = startOfWeek(subWeeks(now, 1), weekOpts);
    return {
      start: weekStart,
      end: now,
      previousStart: prevWeekStart,
      previousEnd: subWeeks(now, 1),
      isAllTime: false,
      cacheKey: 'week'
    };
  }

  if (dateRange === 'month') {
    // Compare this-month-to-date against last-month-up-to-the-same-day,
    // so the % change in the KPIs is apples-to-apples (e.g. "up 21% vs
    // this date last month") instead of a partial-vs-full distortion.
    const monthStart = startOfMonth(now);
    const prevMonthStart = startOfMonth(subMonths(now, 1));
    return {
      start: monthStart,
      end: now,
      previousStart: prevMonthStart,
      previousEnd: subMonths(now, 1),
      isAllTime: false,
      cacheKey: 'month'
    };
  }

  // Specific month: '2026-02'. For the current month, cap previous comparison
  // at the same day-of-month so it stays month-to-date.
  if (typeof dateRange === 'string' && /^\d{4}-\d{2}$/.test(dateRange)) {
    const [year, month] = dateRange.split('-').map(Number);
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = endOfMonth(monthStart);
    const prevMonthStart = startOfMonth(subMonths(monthStart, 1));
    const isCurrentMonth = monthEnd > now;
    const end = isCurrentMonth ? now : monthEnd;
    const previousEnd = isCurrentMonth ? subMonths(now, 1) : endOfMonth(subMonths(monthStart, 1));
    return {
      start: monthStart,
      end,
      previousStart: prevMonthStart,
      previousEnd,
      isAllTime: false,
      cacheKey: dateRange
    };
  }

  // Specific week: '2026-W13' (ISO week number)
  if (typeof dateRange === 'string' && /^\d{4}-W\d{1,2}$/.test(dateRange)) {
    const [yearStr, weekStr] = dateRange.split('-W');
    const year = Number(yearStr);
    const week = Number(weekStr);

    // Calculate the start of the ISO week (Monday-based)
    // Jan 4 is always in ISO week 1
    const jan4 = new Date(year, 0, 4);
    const jan4DayOfWeek = jan4.getDay() || 7; // Convert Sunday=0 to 7
    const week1Monday = new Date(jan4);
    week1Monday.setDate(jan4.getDate() - (jan4DayOfWeek - 1));

    const weekStart = new Date(week1Monday);
    weekStart.setDate(week1Monday.getDate() + (week - 1) * 7);
    const wkEnd = new Date(weekStart);
    wkEnd.setDate(weekStart.getDate() + 6);
    wkEnd.setHours(23, 59, 59, 999);

    const prevWeekStart = new Date(weekStart);
    prevWeekStart.setDate(weekStart.getDate() - 7);
    // If the selected week is the current (in-progress) week, cap the
    // previous comparison at the same elapsed offset so it stays
    // week-to-date — matches the 'week' shortcut + the month logic.
    const isCurrentWeek = wkEnd > now;
    const end = isCurrentWeek ? now : wkEnd;
    let previousEnd;
    if (isCurrentWeek) {
      previousEnd = new Date(now);
      previousEnd.setDate(now.getDate() - 7);
    } else {
      previousEnd = new Date(prevWeekStart);
      previousEnd.setDate(prevWeekStart.getDate() + 6);
      previousEnd.setHours(23, 59, 59, 999);
    }

    return {
      start: weekStart,
      end,
      previousStart: prevWeekStart,
      previousEnd,
      isAllTime: false,
      cacheKey: dateRange
    };
  }

  // Fallback: treat as number of days (backwards compat)
  const days = Number(dateRange) || 30;
  const start = subDays(now, days - 1);
  const previousStart = subDays(start, days);
  return {
    start,
    end: now,
    previousStart,
    previousEnd: start,
    isAllTime: false,
    cacheKey: String(days)
  };
};

/**
 * Get cached data or fetch new data.
 *
 * Two-tier cache: in-memory Map (fastest) backed by localStorage so the
 * dashboard survives F5 / tab close without re-hitting Firestore. Both
 * tiers share the same TTL.
 */
const getCachedOrFetch = async (key, fetchFn, ttl = CACHE_TTL) => {
  // L1: in-memory
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < ttl) {
    return cached.data;
  }

  // L2: localStorage (survives reloads). Wrapped in try/catch because
  // localStorage may be disabled (private mode), quota-exceeded, or contain
  // schema-incompatible entries from a prior deploy.
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Date.now() - parsed.timestamp < ttl) {
        cache.set(key, parsed);
        return parsed.data;
      }
    }
  } catch (_e) { /* noop */ }

  const data = await fetchFn();
  const entry = { data, timestamp: Date.now() };
  cache.set(key, entry);
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
  } catch (_e) {
    // Quota — purge our prefix and retry once. If still failing, give up:
    // memory cache still works, we just lose the cross-reload benefit.
    try {
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(STORAGE_PREFIX)) toRemove.push(k);
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
    } catch (_e2) { /* noop */ }
  }
  return data;
};

/**
 * Shared raw-document fetchers.
 *
 * The dashboard widgets (executive KPIs, revenue timeline, booth leaderboard,
 * revenue-by-booth, package performance) all need the SAME sessions /
 * stripe_payments / booths documents for a given date range — org scoping and
 * internal-session exclusion happen client-side, so the underlying Firestore
 * queries are identical. These fetchers memoize the raw document snapshots
 * (memory tier only — snapshots aren't JSON-serializable, so they never touch
 * localStorage) and de-duplicate concurrent in-flight calls, so one dashboard
 * load reads each collection once instead of once per widget.
 *
 * Keys deliberately EXCLUDE org scoping: the raw range query is org-agnostic,
 * so switching orgs reuses the same raw docs.
 */
const RAW_PREFIX = 'raw:';
const inflightRaw = new Map();

const getSharedDocs = (key, fetchFn) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return Promise.resolve(cached.data);
  }
  if (inflightRaw.has(key)) return inflightRaw.get(key);
  const promise = (async () => {
    try {
      const data = await fetchFn();
      cache.set(key, { data, timestamp: Date.now() });
      return data;
    } finally {
      inflightRaw.delete(key);
    }
  })();
  inflightRaw.set(key, promise);
  return promise;
};

/** All sessions in the range. Same query every widget used to run itself. */
const fetchSessionDocsInRange = (range) =>
  getSharedDocs(`${RAW_PREFIX}sessions-${range.cacheKey}`, async () => {
    const cap = range.isAllTime ? ALL_TIME_LIMIT : SAFETY_LIMIT;
    const sessionsQuery = range.isAllTime
      ? query(collection(db, 'sessions'), orderBy('timestamp', 'desc'), limit(ALL_TIME_LIMIT))
      : query(
          collection(db, 'sessions'),
          where('timestamp', '>=', Timestamp.fromDate(range.start)),
          where('timestamp', '<=', Timestamp.fromDate(range.end)),
          limit(SAFETY_LIMIT)
        );
    const snap = await getDocs(sessionsQuery);
    checkTruncation(snap, cap, `shared/sessions/${range.cacheKey}`);
    return snap.docs;
  });

/** Sessions in the previous comparison window (KPI trend math). */
const fetchPreviousSessionDocs = (range) =>
  getSharedDocs(`${RAW_PREFIX}sessions-prev-${range.cacheKey}`, async () => {
    const snap = await getDocs(query(
      collection(db, 'sessions'),
      where('timestamp', '>=', Timestamp.fromDate(range.previousStart)),
      where('timestamp', '<=', Timestamp.fromDate(range.previousEnd)),
      limit(SAFETY_LIMIT)
    ));
    checkTruncation(snap, SAFETY_LIMIT, `shared/sessions-prev/${range.cacheKey}`);
    return snap.docs;
  });

/** Completed payments in the range. */
const fetchPaymentDocsInRange = (range) =>
  getSharedDocs(`${RAW_PREFIX}payments-${range.cacheKey}`, async () => {
    const cap = range.isAllTime ? ALL_TIME_LIMIT : SAFETY_LIMIT;
    const paymentsQuery = range.isAllTime
      ? query(collection(db, 'stripe_payments'), where('status', '==', 'completed'), limit(ALL_TIME_LIMIT))
      : query(
          collection(db, 'stripe_payments'),
          where('status', '==', 'completed'),
          where('created_at', '>=', Timestamp.fromDate(range.start)),
          where('created_at', '<=', Timestamp.fromDate(range.end)),
          limit(SAFETY_LIMIT)
        );
    const snap = await getDocs(paymentsQuery);
    checkTruncation(snap, cap, `shared/payments/${range.cacheKey}`);
    return snap.docs;
  });

/** Completed payments in the previous comparison window (KPI trend math). */
const fetchPreviousPaymentDocs = (range) =>
  getSharedDocs(`${RAW_PREFIX}payments-prev-${range.cacheKey}`, async () => {
    const snap = await getDocs(query(
      collection(db, 'stripe_payments'),
      where('status', '==', 'completed'),
      where('created_at', '>=', Timestamp.fromDate(range.previousStart)),
      where('created_at', '<=', Timestamp.fromDate(range.previousEnd)),
      limit(SAFETY_LIMIT)
    ));
    checkTruncation(snap, SAFETY_LIMIT, `shared/payments-prev/${range.cacheKey}`);
    return snap.docs;
  });

/** All booths (leaderboard + by-booth timeline both need the full list). */
const fetchBoothDocs = () =>
  getSharedDocs(`${RAW_PREFIX}booths`, async () => {
    const snap = await getDocs(query(collection(db, 'booths'), limit(SAFETY_LIMIT)));
    checkTruncation(snap, SAFETY_LIMIT, 'shared/booths');
    return snap.docs;
  });

/**
 * ALL-TIME chart source: daily_stats rollups.
 *
 * The cloud's nightly generateDailyStats job (plus the backfillDailyStats
 * callable for history) maintains one doc per booth per day WITH activity in
 * `daily_stats` — see socialsnap-cloud/firebase-functions/dailyStats.js for
 * the schema. The all-time charts (revenue timeline, booth leaderboard,
 * revenue-by-booth) read these EXACT rollups instead of raw sessions/payments
 * capped at ALL_TIME_LIMIT docs. Volume is bounded by booths × days
 * (~a few docs/day), so DAILY_STATS_LIMIT covers decades; the truncation
 * alarm still guards the pathological case.
 *
 * Org scoping happens server-side via booth_id `in` chunks (per-booth docs
 * make it trivial — orgBoothIds is already the org's booth list). Requires
 * the (booth_id, date) composite index; a missing index or an empty (not yet
 * backfilled) collection makes callers fall back to the doc path.
 *
 * Rollup days are bucketed by the SERVER's calendar (UTC in production) and
 * end at YESTERDAY; today's partial day is appended client-side from the
 * shared 'today' windowed fetch (see fetchAllTimeStatRows). With US-based
 * viewers (west of UTC) the two sources never overlap; at worst the last few
 * hours before local midnight lag until the next nightly rollup.
 */
// Firestore rejects query limits above 10,000 — page with a cursor instead.
// One page is ~27 booth-years of rollup rows; the page cap is purely a
// runaway guard.
const DAILY_STATS_PAGE = 10000;
const DAILY_STATS_MAX_PAGES = 5;
const FIRESTORE_IN_LIMIT = 30;
const UNATTRIBUTED_KEY = '_unattributed';

const fetchDailyStatPages = async (baseConstraints, label) => {
  const docs = [];
  let cursor = null;
  for (let page = 0; page < DAILY_STATS_MAX_PAGES; page += 1) {
    const constraints = [...baseConstraints, orderBy('date', 'asc'), limit(DAILY_STATS_PAGE)];
    if (cursor) constraints.push(startAfter(cursor));
    const snap = await getDocs(query(collection(db, 'daily_stats'), ...constraints));
    docs.push(...snap.docs);
    if (snap.docs.length < DAILY_STATS_PAGE) return docs;
    cursor = snap.docs[snap.docs.length - 1];
  }
  console.warn(`[analytics] Query "${label}" hit the ${DAILY_STATS_MAX_PAGES}-page guard — likely truncated.`);
  return docs;
};

const fetchDailyStatDocs = (orgBoothIds) => {
  const orgKey = orgBoothIds ? orgBoothIds.join(',') : 'all';
  return getSharedDocs(`${RAW_PREFIX}daily-stats-${orgKey}`, async () => {
    if (!orgBoothIds) {
      return fetchDailyStatPages([], 'shared/daily-stats/all');
    }
    const chunks = [];
    for (let i = 0; i < orgBoothIds.length; i += FIRESTORE_IN_LIMIT) {
      chunks.push(orgBoothIds.slice(i, i + FIRESTORE_IN_LIMIT));
    }
    const snaps = await Promise.all(chunks.map((chunk) =>
      fetchDailyStatPages([where('booth_id', 'in', chunk)], 'shared/daily-stats/org')));
    const docs = snaps.flat();
    // Chunked queries arrive independently ordered — merge-sort by date.
    docs.sort((a, b) => (a.data().date < b.data().date ? -1 : a.data().date > b.data().date ? 1 : 0));
    return docs;
  });
};

/** Parse a daily_stats 'yyyy-MM-dd' date string as a LOCAL calendar date. */
const parseDailyDate = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** Org scoping over rollup rows: unattributed rows only count in all-orgs. */
const filterStatRows = (statRows, orgBoothIds) => (orgBoothIds
  ? statRows.filter((row) => row.booth_id && orgBoothIds.includes(row.booth_id))
  : statRows);

/**
 * Client-side twin of the cloud's aggregateDailyStats: collapses TODAY's raw
 * session/payment docs into daily_stats-shaped rows so the rollup-sourced
 * charts include the in-progress day. Exported for unit tests.
 */
export const aggregateDocsToDailyRows = (dateStr, sessionDocs, paymentDocs) => {
  const rows = new Map();
  const getRow = (boothKey) => {
    let row = rows.get(boothKey);
    if (!row) {
      row = {
        date: dateStr,
        booth_id: boothKey === UNATTRIBUTED_KEY ? null : boothKey,
        sessions: 0,
        internal_sessions: 0,
        photos: 0,
        payments: 0,
        revenue_cents: 0
      };
      rows.set(boothKey, row);
    }
    return row;
  };

  // Session-id → booth map for payments missing the booth_id stamp.
  const sessionIdToBoothId = new Map();
  sessionDocs.forEach((doc) => {
    const data = doc.data();
    if (!data.booth_id) return;
    sessionIdToBoothId.set(doc.id, data.booth_id);
    if (data.anonymous_session_id) {
      sessionIdToBoothId.set(data.anonymous_session_id, data.booth_id);
    }
  });

  sessionDocs.forEach((doc) => {
    const data = doc.data();
    const row = getRow(data.booth_id || UNATTRIBUTED_KEY);
    if (isInternalSession(data)) {
      row.internal_sessions += 1;
    } else {
      row.sessions += 1;
      row.photos += data.photos?.length ?? data.photo_count ?? 0;
    }
  });

  paymentDocs.forEach((doc) => {
    const data = doc.data();
    if (!isCountedAsRevenue(data)) return;
    const boothKey = data.booth_id
      || (data.anonymous_session_id && sessionIdToBoothId.get(data.anonymous_session_id))
      || UNATTRIBUTED_KEY;
    const row = getRow(boothKey);
    row.payments += 1;
    row.revenue_cents += data.amount || 0;
  });

  return Array.from(rows.values());
};

/**
 * Fetch the full all-time rollup rows: every daily_stats row (org-scoped when
 * requested) plus today's partial day derived from the shared 'today'
 * windowed fetch. Returns null when the collection has no rows for the scope
 * (not yet backfilled / brand-new org) so callers fall back to the doc path.
 */
/** Group docs by the UTC calendar day of a timestamp field (rollup convention). */
const groupDocsByUtcDay = (docs, field) => {
  const byDay = new Map();
  docs.forEach((doc) => {
    const v = doc.data()[field];
    const d = v?.toDate ? v.toDate() : v instanceof Date ? v : null;
    if (!d || Number.isNaN(d.getTime())) return;
    const day = d.toISOString().slice(0, 10);
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day).push(doc);
  });
  return byDay;
};

const fetchAllTimeStatRows = async (orgBoothIds) => {
  const statDocs = await fetchDailyStatDocs(orgBoothIds);
  if (!statDocs || statDocs.length === 0) return null;
  const rows = statDocs.map((doc) => doc.data());

  // Live tail: everything AFTER the last rollup, bucketed by the same UTC
  // days the rollups use. Rollup days end at UTC midnight — a local-midnight
  // "today" fetch would drop the seam hours between the two (8 PM–midnight
  // ET; observed live as a ~71-session undercount on a Friday evening).
  const lastRollupDate = rows[rows.length - 1].date;
  const liveStart = new Date(Date.parse(`${lastRollupDate}T00:00:00Z`) + 24 * 60 * 60 * 1000);
  const now = new Date();
  if (liveStart >= now) return rows;

  const liveRange = {
    start: liveStart,
    end: now,
    isAllTime: false,
    cacheKey: `since-${lastRollupDate}`
  };
  const [sessionDocs, paymentDocs] = await Promise.all([
    fetchSessionDocsInRange(liveRange),
    fetchPaymentDocsInRange(liveRange)
  ]);

  const sessionsByDay = groupDocsByUtcDay(sessionDocs, 'timestamp');
  const paymentsByDay = groupDocsByUtcDay(paymentDocs, 'created_at');
  const liveDays = [...new Set([...sessionsByDay.keys(), ...paymentsByDay.keys()])].sort();
  const liveRows = liveDays.flatMap((day) =>
    aggregateDocsToDailyRows(day, sessionsByDay.get(day) || [], paymentsByDay.get(day) || []));
  return rows.concat(liveRows);
};

/** Shared warn/Sentry plumbing for rollup-path failures (charts fall back). */
const reportRollupFallback = (chart, error) => {
  console.warn(`[analytics] All-time ${chart} rollup path failed; falling back to capped doc path:`, error);
  try {
    Sentry.captureException(error, {
      tags: { service: 'analytics', operation: `all_time_${chart}_rollup` }
    });
  } catch (_e) { /* noop */ }
};

/**
 * EXACT all-time KPI totals via server-side aggregation.
 *
 * The all-time doc fetchers above cap at ALL_TIME_LIMIT (10k) docs, so once a
 * collection grows past the cap the doc-derived totals silently underreport.
 * Firestore aggregations (count / sum) have no such cap, but they can't
 * directly express our missing-field filter semantics:
 *   - a session with NO initiated_by_role is a customer session
 *   - a completed payment with NO dispute_status (or 'won') counts as revenue
 * Both become exact via subtraction identities:
 *
 *   customer sessions = count(all) − count(initiated_by_role in INTERNAL_ROLES)
 *     Exact because isInternalSession() is true iff initiated_by_role is one
 *     of INTERNAL_ROLES — everything else (missing field included) is customer.
 *
 *   revenue = sum(amount | status=='completed')
 *           − sum(amount | status=='completed' AND dispute_status != 'won')
 *     Exact because Firestore's `!=` matches only docs where the field EXISTS
 *     and differs — precisely isCountedAsRevenue()'s excluded set ("has a
 *     dispute_status other than 'won'"). sum() ignores docs missing `amount`,
 *     matching the client loop's `data.amount || 0`.
 *
 *   photos = sum(photo_count) − sum(photo_count | initiated_by_role in [...])
 *     NOTE: photo_count is a denormalized field maintained by the cloud's
 *     onSessionWriteSyncPhotoCount trigger. It is only exact AFTER the
 *     backfillSessionPhotoCounts callable has run — until then, sessions
 *     without photo_count contribute 0 (an undercount, same failure mode as
 *     the old capped doc path).
 *
 * Org scoping: org sessions are exactly "booth_id in orgBoothIds" (the doc
 * path drops sessions without a booth_id when scoped). Aggregate queries
 * only reliably support ONE `in` disjunction, so we loop booths with
 * booth_id == X per query (initiated_by_role `in` stays the single
 * disjunction, 4 values ≤ 30 limit) and sum client-side. Aggregate reads are
 * cheap (1 read / 1000 index entries), so a few dozen queries are fine.
 *
 * Org-scoped all-time REVENUE cannot be served this way yet: payments have
 * no org linkage until the backfillPaymentOrgs backfill runs (scoping goes
 * through session-id joins client-side). It stays on the doc path — once the
 * backfill has run it can switch to where('organization_id', '==', orgId)
 * aggregate subtraction.
 *
 * Each aggregation runs as its OWN query on purpose: Firestore documents that
 * combining aggregations over different fields restricts the calculation to
 * documents containing ALL those fields, which would corrupt count() when
 * some sessions still lack photo_count.
 *
 * These queries bypass the 10k doc caps entirely, so the truncation alarm is
 * intentionally absent here; it stays on the shared doc fetchers, which the
 * timeline/leaderboard widgets (still doc-based, they need docs for
 * bucketing) continue to use.
 */

const countSessionsAgg = async (constraints) => {
  const snap = await getAggregateFromServer(
    query(collection(db, 'sessions'), ...constraints),
    { value: count() }
  );
  return snap.data().value;
};

const sumSessionPhotosAgg = async (constraints) => {
  const snap = await getAggregateFromServer(
    query(collection(db, 'sessions'), ...constraints),
    { value: sum('photo_count') }
  );
  return snap.data().value;
};

const sumPaymentAmountsAgg = async (constraints) => {
  const snap = await getAggregateFromServer(
    query(collection(db, 'stripe_payments'), ...constraints),
    { value: sum('amount') }
  );
  return snap.data().value;
};

/**
 * Run the aggregate queries for the all-time view. Each KPI runs as an
 * INDEPENDENT leg with its own fallback: aggregate queries don't use index
 * prefixes, so a missing composite for one leg (e.g. the payments sum) must
 * not drag the other exact numbers back to the capped doc path. Returns
 * { sessions, photos, revenue } with null for any leg that failed or isn't
 * servable (org-scoped revenue pre-backfill); null when nothing succeeded.
 */
const settleAgg = (promise, leg) =>
  promise.catch((error) => {
    console.warn(`[analytics] All-time ${leg} aggregate failed; keeping doc-based number:`, error);
    try {
      Sentry.captureException(error, {
        tags: { service: 'analytics', operation: `all_time_${leg}_aggregate` }
      });
    } catch (_e) { /* noop */ }
    return null;
  });

const fetchAllTimeExactTotals = async (orgBoothIds) => {
  const internalFilter = where('initiated_by_role', 'in', INTERNAL_ROLES);
  const scopes = orgBoothIds
    ? orgBoothIds.map((boothId) => [where('booth_id', '==', boothId)])
    : [[]];

  const subtractAcrossScopes = (aggFn) =>
    Promise.all(scopes.map(async (base) => {
      const [total, internal] = await Promise.all([
        aggFn(base),
        aggFn([...base, internalFilter])
      ]);
      return total - internal;
    })).then((perScope) => perScope.reduce((a, b) => a + b, 0));

  const sessionsPromise = settleAgg(subtractAcrossScopes(countSessionsAgg), 'sessions');
  const photosPromise = settleAgg(subtractAcrossScopes(sumSessionPhotosAgg), 'photos');

  // Revenue subtraction is only org-agnostic for now (see block comment).
  const revenuePromise = orgBoothIds
    ? Promise.resolve(null)
    : settleAgg(Promise.all([
        sumPaymentAmountsAgg([where('status', '==', 'completed')]),
        sumPaymentAmountsAgg([
          where('status', '==', 'completed'),
          where('dispute_status', '!=', 'won')
        ])
      ]).then(([completed, excluded]) => completed - excluded), 'revenue');

  const [sessions, photos, revenue] = await Promise.all([
    sessionsPromise,
    photosPromise,
    revenuePromise
  ]);

  if (sessions === null && photos === null && revenue === null) return null;
  return { sessions, photos, revenue };
};

/**
 * Clear cache for all keys, or every key starting with the given prefix
 * (an exact key is its own prefix, so passing a full key still works).
 * Clears the memory tier, in-flight shared fetches, and localStorage.
 */
export const clearCache = (keyPrefix = null) => {
  if (keyPrefix) {
    for (const k of Array.from(cache.keys())) {
      if (k.startsWith(keyPrefix)) cache.delete(k);
    }
    for (const k of Array.from(inflightRaw.keys())) {
      if (k.startsWith(keyPrefix)) inflightRaw.delete(k);
    }
    try {
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(STORAGE_PREFIX + keyPrefix)) toRemove.push(k);
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
    } catch (_e) { /* noop */ }
  } else {
    cache.clear();
    inflightRaw.clear();
    try {
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(STORAGE_PREFIX)) toRemove.push(k);
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
    } catch (_e) { /* noop */ }
  }
};

/**
 * Invalidate only the cache entries behind ONE dashboard view (a specific
 * date range + org scope): the computed widget results plus the shared raw
 * fetches for that range. Used by the dashboard's 5-minute auto-refresh so
 * other ranges' still-valid cache entries survive and switching back to them
 * within their TTL stays instant.
 */
export const clearDashboardCache = (dateRange = 'month', orgBoothIds = null) => {
  const range = resolveDateRange(dateRange);
  const orgKey = orgBoothIds ? orgBoothIds.join(',') : 'all';
  [
    `executive-kpis-${range.cacheKey}-${orgKey}`,
    `revenue-timeline-${range.cacheKey}-${orgKey}`,
    `package-performance-${range.cacheKey}-${orgKey}`,
    `booth-leaderboard-${range.cacheKey}-${orgKey}`,
    `revenue-by-booth-${range.cacheKey}-${orgKey}`,
    `${RAW_PREFIX}sessions-${range.cacheKey}`,
    `${RAW_PREFIX}sessions-prev-${range.cacheKey}`,
    `${RAW_PREFIX}payments-${range.cacheKey}`,
    `${RAW_PREFIX}payments-prev-${range.cacheKey}`,
    `${RAW_PREFIX}daily-stats-${orgKey}`,
    `${RAW_PREFIX}booths`
  ].forEach((key) => clearCache(key));
};

/**
 * Helper: Filter sessions by booth IDs AND drop staff-initiated sessions.
 * Analytics + payouts should never include sessions started by superadmins /
 * admins / org_owners / salespeople — those are internal usage, not revenue.
 * Identification is via the stamped `initiated_by_role` field; legacy
 * sessions without it are treated as customer (matches historical reports).
 */
const filterSessionsByBooths = (sessions, boothIds) => {
  const noInternal = sessions.filter((session) => !isInternalSession(session));
  if (!boothIds || boothIds.length === 0) return noInternal;
  return noInternal.filter((session) => {
    const boothId = session.data().booth_id;
    return boothId && boothIds.includes(boothId);
  });
};

/**
 * Helper: Get session IDs from sessions for payment filtering
 */
const getSessionIdsFromSessions = (sessions) => {
  const sessionIds = new Set();
  sessions.forEach(session => {
    const data = session.data();
    if (data.anonymous_session_id) {
      sessionIds.add(data.anonymous_session_id);
    }
    sessionIds.add(session.id);
  });
  return sessionIds;
};

/**
 * Helper: Filter payments by session IDs
 */
const filterPaymentsBySessions = (payments, sessionIds) => {
  if (!sessionIds || sessionIds.size === 0) return payments;
  return payments.filter(payment => {
    const anonSessionId = payment.data().anonymous_session_id;
    return anonSessionId && sessionIds.has(anonSessionId);
  });
};

/**
 * Pure aggregation for getExecutiveKPIs. Exported for unit tests.
 * Same math the function previously ran inline over its own fetches.
 */
export const computeExecutiveKPIs = ({
  dateRange,
  isAllTime,
  now,
  orgBoothIds,
  currentSessionDocs,
  previousSessionDocs,
  currentPaymentDocs,
  previousPaymentDocs,
  totalUsersCount,
  newUserDocs,
  exactAllTimeTotals = null
}) => {
  // Legacy shape — downstream code uses `users.size` and `users.docs`.
  const users = {
    size: totalUsersCount,
    docs: newUserDocs
  };

  // Filter out staff-initiated sessions (always) and by org booths (if any).
  const wrap = (docs) => ({ docs, size: docs.length, forEach: (fn) => docs.forEach(fn) });
  const currentSessions = wrap(filterSessionsByBooths(currentSessionDocs, orgBoothIds));
  const previousSessions = wrap(filterSessionsByBooths(previousSessionDocs, orgBoothIds));

  // Filter payments by sessions — only when an org-scoped view is active.
  // For all-orgs (superadmin), pass payments through raw so legitimate
  // payments without a discoverable session linkage still count.
  const currentSessionIds = orgBoothIds ? getSessionIdsFromSessions(currentSessions.docs) : null;
  const previousSessionIds = orgBoothIds ? getSessionIdsFromSessions(previousSessions.docs) : null;
  const currentPayments = orgBoothIds
    ? wrap(filterPaymentsBySessions(currentPaymentDocs, currentSessionIds))
    : wrap(currentPaymentDocs);
  const previousPayments = orgBoothIds
    ? wrap(filterPaymentsBySessions(previousPaymentDocs, previousSessionIds))
    : wrap(previousPaymentDocs);

  // Calculate current metrics
  let currentRevenue = 0;
  currentPayments.forEach(doc => {
    const data = doc.data();
    if (!isCountedAsRevenue(data)) return;
    currentRevenue += data.amount || 0;
  });

  let previousRevenue = 0;
  previousPayments.forEach(doc => {
    const data = doc.data();
    if (!isCountedAsRevenue(data)) return;
    previousRevenue += data.amount || 0;
  });

  let currentPhotos = 0;
  let currentSessionCount = currentSessions.size;
  currentSessions.forEach(doc => {
    currentPhotos += (doc.data().photos?.length || 0);
  });

  let previousPhotos = 0;
  const previousSessionCount = previousSessions.size;
  previousSessions.forEach(doc => {
    previousPhotos += (doc.data().photos?.length || 0);
  });

  // All-time only: exact server-side aggregate totals (subtraction
  // identities — see fetchAllTimeExactTotals) replace the doc-derived
  // top-line numbers, which are capped at ALL_TIME_LIMIT docs. Sparklines
  // stay doc-based: they only cover the last 7 days, which is always inside
  // the most-recent-10k window. revenue is null when the aggregate path
  // can't express the scope (org-scoped, pending the payment-org backfill) —
  // the doc-based figure stays authoritative there.
  if (isAllTime && exactAllTimeTotals) {
    // Per-leg: any leg that failed its aggregate arrives as null and keeps
    // the doc-derived number instead.
    if (exactAllTimeTotals.sessions != null) {
      currentSessionCount = exactAllTimeTotals.sessions;
    }
    if (exactAllTimeTotals.photos != null) {
      currentPhotos = exactAllTimeTotals.photos;
    }
    if (exactAllTimeTotals.revenue != null) {
      currentRevenue = exactAllTimeTotals.revenue;
    }
  }

  // Calculate percentage changes
  const revenueChange = isAllTime ? 0 : (previousRevenue > 0
    ? ((currentRevenue - previousRevenue) / previousRevenue) * 100
    : 100);

  const sessionChange = isAllTime ? 0 : (previousSessionCount > 0
    ? ((currentSessionCount - previousSessionCount) / previousSessionCount) * 100
    : 100);

  const photoChange = isAllTime ? 0 : (previousPhotos > 0
    ? ((currentPhotos - previousPhotos) / previousPhotos) * 100
    : 100);

  // Generate sparkline data (last 7 days)
  const last7Days = eachDayOfInterval({
    start: subDays(now, 6),
    end: now
  });

  const sparklineData = {
    revenue: last7Days.map(day => {
      const dayRevenue = Array.from(currentPayments.docs)
        .filter(doc => {
          const data = doc.data();
          if (!isCountedAsRevenue(data)) return false;
          const created = data.created_at?.toDate();
          return created && format(created, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd');
        })
        .reduce((sum, doc) => sum + (doc.data().amount || 0), 0);

      return {
        date: format(day, 'MMM dd'),
        value: dayRevenue / 100 // Convert to dollars
      };
    }),
    sessions: last7Days.map(day => {
      const daySessions = Array.from(currentSessions.docs)
        .filter(doc => {
          const timestamp = doc.data().timestamp?.toDate();
          return timestamp && format(timestamp, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd');
        }).length;

      return {
        date: format(day, 'MMM dd'),
        value: daySessions
      };
    }),
    photos: last7Days.map(day => {
      const dayPhotos = Array.from(currentSessions.docs)
        .filter(doc => {
          const timestamp = doc.data().timestamp?.toDate();
          return timestamp && format(timestamp, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd');
        })
        .reduce((sum, doc) => sum + (doc.data().photos?.length || 0), 0);

      return {
        date: format(day, 'MMM dd'),
        value: dayPhotos
      };
    }),
    users: last7Days.map(day => {
      const dayUsers = Array.from(users.docs)
        .filter(doc => {
          const created = doc.data().created_at?.toDate();
          return created && format(created, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd');
        }).length;

      return {
        date: format(day, 'MMM dd'),
        value: dayUsers
      };
    })
  };

  const trendLabel = isAllTime ? 'all time'
    : dateRange === 'today' ? 'vs yesterday'
    : dateRange === 'week' ? 'vs this day last week'
    : dateRange === 'month' ? 'vs this day last month'
    : typeof dateRange === 'string' && /^\d{4}-\d{2}$/.test(dateRange) ? 'vs prior month'
    : `vs prior period`;

  return {
    revenue: {
      current: currentRevenue / 100, // Convert to dollars
      change: Math.round(revenueChange * 10) / 10,
      trend: trendLabel,
      sparkline: sparklineData.revenue
    },
    sessions: {
      current: currentSessionCount,
      change: Math.round(sessionChange * 10) / 10,
      trend: trendLabel,
      sparkline: sparklineData.sessions
    },
    photos: {
      current: currentPhotos,
      change: Math.round(photoChange * 10) / 10,
      trend: `${(currentPhotos / Math.max(currentSessionCount, 1)).toFixed(1)} avg per session`,
      sparkline: sparklineData.photos
    },
    users: {
      current: users.size,
      change: 0, // We'd need historical data for this
      trend: `total users`,
      sparkline: sparklineData.users
    }
  };
};

/**
 * Get executive KPIs with trend data
 * @param {string|null} dateRange - 'week', 'month', '2026-02', or null for all time
 * @param {Array<string>} orgBoothIds - Optional array of booth IDs to filter by organization
 */
export const getExecutiveKPIs = async (dateRange = 'month', orgBoothIds = null) => {
  const range = resolveDateRange(dateRange);
  const cacheKey = `executive-kpis-${range.cacheKey}-${orgBoothIds ? orgBoothIds.join(',') : 'all'}`;

  return getCachedOrFetch(cacheKey, async () => {
    const now = new Date();
    const { isAllTime } = range;

    // Sessions/payments come from the shared range fetchers — one read per
    // collection per range, shared with the timeline/leaderboard widgets.
    // The users total uses getCountFromServer (1 read aggregation) and the
    // new-users sparkline reads only the last 7 days — together they replace
    // what used to be a full users-collection fetch on every dashboard view.
    //
    // Windowed ranges keep the doc-fetch path for session counts / photo
    // sums / revenue sums: the same docs feed the sparklines and the
    // timeline's bucketing anyway, and windowed volumes sit well under the
    // caps. The ALL-TIME view additionally runs exact server-side aggregate
    // totals (subtraction identities over count()/sum() — see
    // fetchAllTimeExactTotals) because its doc fetch is capped at
    // ALL_TIME_LIMIT and would silently underreport past 10k docs.
    const sparklineStart = subDays(now, 6);

    // All-time: run the exact aggregates FIRST. When every leg succeeds, the
    // session/payment docs are only needed for the 7-day sparklines — a slim
    // 7-day fetch replaces the capped 10k pulls (which existed as the totals
    // fallback), saving ~20k reads per all-time load and retiring the
    // misleading "returned exactly 10000 docs" truncation warning. Any
    // failed leg (or org scoping, whose revenue fallback joins payments to
    // the FULL session list) keeps the capped fetch so fallback numbers stay
    // correct.
    const exactAllTimeTotals = isAllTime
      ? await fetchAllTimeExactTotals(orgBoothIds)
      : null;
    const allLegsExact = exactAllTimeTotals
      && exactAllTimeTotals.sessions != null
      && exactAllTimeTotals.photos != null
      && exactAllTimeTotals.revenue != null;
    const docRange = isAllTime && allLegsExact
      ? {
          start: startOfDay(sparklineStart),
          end: now,
          isAllTime: false,
          cacheKey: 'sparkline-7d'
        }
      : range;

    const [
      currentSessionDocs,
      previousSessionDocs,
      currentPaymentDocs,
      previousPaymentDocs,
      usersCountSnap,
      affiliateCountSnap,
      newUsersSnap
    ] = await Promise.all([
      fetchSessionDocsInRange(docRange),
      isAllTime ? Promise.resolve([]) : fetchPreviousSessionDocs(range),
      fetchPaymentDocsInRange(docRange),
      isAllTime ? Promise.resolve([]) : fetchPreviousPaymentDocs(range),
      getCountFromServer(collection(db, 'users')),
      // Affiliates are program partners, not platform users, and demo
      // affiliate accounts must never move real numbers — subtract them from
      // the total. Equality query (not '!=') so legacy users docs without a
      // role field stay counted; count failure degrades to subtracting 0
      // rather than sinking the whole KPI load.
      getCountFromServer(query(collection(db, 'users'), where('role', '==', 'affiliate')))
        .catch(() => ({ data: () => ({ count: 0 }) })),
      getDocs(query(
        collection(db, 'users'),
        where('created_at', '>=', Timestamp.fromDate(startOfDay(sparklineStart))),
        limit(SAFETY_LIMIT)
      ))
    ]);

    checkTruncation(newUsersSnap, SAFETY_LIMIT, `executive-kpis/new-users-7d`);

    return computeExecutiveKPIs({
      dateRange,
      isAllTime,
      now,
      orgBoothIds,
      currentSessionDocs,
      previousSessionDocs,
      currentPaymentDocs,
      previousPaymentDocs,
      totalUsersCount: Math.max(0, usersCountSnap.data().count - affiliateCountSnap.data().count),
      // Same exclusion for the new-users sparkline: a freshly invited
      // affiliate (or any demo-flagged account) is not a new customer.
      newUserDocs: newUsersSnap.docs.filter((d) => {
        const u = d.data() || {};
        return u.role !== 'affiliate' && u.isDemo !== true;
      }),
      exactAllTimeTotals
    });
  });
};

/**
 * Shared time-bucketing for the timeline charts: hourly for <=1 day, daily
 * for <=90, weekly for <=365, monthly beyond. Both the raw-doc computes and
 * the daily_stats rollup computes use these so their outputs stay
 * shape-identical.
 */
const bucketModeFor = (start, end) => {
  const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  return totalDays <= 1 ? 'hourly' : totalDays > 365 ? 'monthly' : totalDays > 90 ? 'weekly' : 'daily';
};

const bucketKeyForDate = (bucketMode, date) => {
  // The offset keeps the repeated hour on a DST fall-back day distinct: without
  // it 1AM EDT and 1AM EST share a key, both buckets read the merged total, and
  // that hour is counted twice in the chart (Nov 1 2026).
  if (bucketMode === 'hourly') return format(date, 'yyyy-MM-dd-HHxxx');
  if (bucketMode === 'monthly') return format(startOfMonth(date), 'yyyy-MM-dd');
  if (bucketMode === 'weekly') return format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');
  return format(date, 'yyyy-MM-dd');
};

// weekPrefix: the revenue timeline labels weekly buckets "Week Mar 02" while
// the by-booth chart uses the bare "Mar 02" — preserved verbatim.
const bucketLabelForDate = (bucketMode, bucket, weekPrefix = '') => {
  if (bucketMode === 'hourly') return format(bucket, 'ha');
  if (bucketMode === 'monthly') return format(bucket, 'MMM yyyy');
  if (bucketMode === 'weekly') return `${weekPrefix}${format(bucket, 'MMM dd')}`;
  return format(bucket, 'MMM dd');
};

// Tooltip twin of bucketLabelForDate. Spells the weekday out so a bucket can
// be compared like-for-like ("this Friday vs last Friday") instead of by bare
// date. The axis keeps the short label — weekdays would not fit on the ticks.
const bucketLongLabelForDate = (bucketMode, bucket) => {
  if (bucketMode === 'hourly') return format(bucket, 'EEEE, MMM d · ha');
  if (bucketMode === 'monthly') return format(bucket, 'MMMM yyyy');
  // Weekly buckets always start on a Monday, so naming the weekday is noise.
  if (bucketMode === 'weekly') return `Week of ${format(bucket, 'MMM d, yyyy')}`;
  return format(bucket, 'EEEE, MMM d, yyyy');
};

const bucketDatesFor = (bucketMode, start, end) => (
  bucketMode === 'hourly'
    ? eachHourOfInterval({ start, end })
    : bucketMode === 'monthly'
      ? eachMonthOfInterval({ start, end })
      : bucketMode === 'weekly'
        ? eachWeekOfInterval({ start, end }, { weekStartsOn: 1 })
        : eachDayOfInterval({ start, end })
);

/**
 * Pure aggregation for getRevenueTimeline. Exported for unit tests.
 */
export const computeRevenueTimeline = ({ range, now, orgBoothIds, sessionDocs, paymentDocs }) => {
  const { start: startDate, end: endDate, isAllTime } = range;

  // Drop staff-initiated sessions, then filter by org booths (if any).
  const filteredSessionDocs = filterSessionsByBooths(sessionDocs, orgBoothIds);

  const sessionIds = orgBoothIds ? getSessionIdsFromSessions(filteredSessionDocs) : null;
  const filteredPaymentDocs = orgBoothIds
    ? filterPaymentsBySessions(paymentDocs, sessionIds)
    : paymentDocs;

  // Determine effective start for All Time from actual data
  let effectiveStartDate = startDate;
  if (isAllTime) {
    effectiveStartDate = now;
    filteredPaymentDocs.forEach(doc => {
      const created = doc.data().created_at?.toDate();
      if (created && created < effectiveStartDate) effectiveStartDate = created;
    });
    filteredSessionDocs.forEach(doc => {
      const ts = doc.data().timestamp?.toDate();
      if (ts && ts < effectiveStartDate) effectiveStartDate = ts;
    });
    effectiveStartDate = startOfDay(effectiveStartDate);
  }

  // Use endDate for the chart's end point (not now), so past months don't show future days
  const chartEnd = endDate < now ? endDate : now;

  const bucketMode = bucketModeFor(effectiveStartDate, chartEnd);
  const getBucketKey = (date) => bucketKeyForDate(bucketMode, date);
  const getBucketLabel = (bucket) => bucketLabelForDate(bucketMode, bucket, 'Week ');

  // Pre-process data into maps
  const paymentsByKey = {};
  const sessionsByKey = {};

  filteredPaymentDocs.forEach(doc => {
    const data = doc.data();
    if (!isCountedAsRevenue(data)) return;
    const created = data.created_at?.toDate();
    if (!created) return;
    const key = getBucketKey(created);
    if (!paymentsByKey[key]) paymentsByKey[key] = { revenue: 0, orders: 0 };
    paymentsByKey[key].revenue += (data.amount || 0);
    paymentsByKey[key].orders++;
  });

  filteredSessionDocs.forEach(doc => {
    const ts = doc.data().timestamp?.toDate();
    if (!ts) return;
    const key = getBucketKey(ts);
    sessionsByKey[key] = (sessionsByKey[key] || 0) + 1;
  });

  const buckets = bucketDatesFor(bucketMode, effectiveStartDate, chartEnd);

  return buckets.map(bucket => {
    const key = getBucketKey(bucket);
    const label = getBucketLabel(bucket);
    const bucketData = paymentsByKey[key] || { revenue: 0, orders: 0 };
    const sessions = sessionsByKey[key] || 0;

    return {
      date: label,
      // Axis reads `date`; the tooltip prefers `dateLong`.
      dateLong: bucketLongLabelForDate(bucketMode, bucket),
      revenue: bucketData.revenue / 100,
      orders: bucketData.orders,
      avgOrderValue: bucketData.orders > 0 ? bucketData.revenue / bucketData.orders / 100 : 0,
      sessions
    };
  });
};

/**
 * Rollup-sourced twin of computeRevenueTimeline for the ALL-TIME view.
 * Consumes daily_stats rows (see fetchAllTimeStatRows) instead of raw docs
 * and produces the IDENTICAL output shape. Exported for unit tests.
 */
export const computeRevenueTimelineFromDailyStats = ({ now, orgBoothIds, statRows }) => {
  const rows = filterStatRows(statRows, orgBoothIds);
  if (rows.length === 0) return [];

  let effectiveStartDate = now;
  // The chart must end where the DATA ends, not at local `now`.
  //
  // Rollup rows and the live tail are both keyed by UTC day, while
  // parseDailyDate and the bucket list are local. West of UTC the newest UTC
  // day is tomorrow locally, so from 8 PM ET until midnight the live tail's
  // last row fell outside start..now and its revenue silently vanished from
  // the all-time charts — peak booth hours, and exactly the hours commit
  // a4d683f widened the FETCH to cover without aligning the buckets to match.
  let latestRowDate = null;
  rows.forEach((row) => {
    const day = parseDailyDate(row.date);
    if (day < effectiveStartDate) effectiveStartDate = day;
    if (!latestRowDate || day > latestRowDate) latestRowDate = day;
  });
  effectiveStartDate = startOfDay(effectiveStartDate);
  const chartEnd = latestRowDate && latestRowDate > now ? latestRowDate : now;

  const bucketMode = bucketModeFor(effectiveStartDate, chartEnd);

  const paymentsByKey = {};
  const sessionsByKey = {};
  rows.forEach((row) => {
    const key = bucketKeyForDate(bucketMode, parseDailyDate(row.date));
    if (!paymentsByKey[key]) paymentsByKey[key] = { revenue: 0, orders: 0 };
    paymentsByKey[key].revenue += row.revenue_cents || 0;
    paymentsByKey[key].orders += row.payments || 0;
    sessionsByKey[key] = (sessionsByKey[key] || 0) + (row.sessions || 0);
  });

  return bucketDatesFor(bucketMode, effectiveStartDate, chartEnd).map((bucket) => {
    const key = bucketKeyForDate(bucketMode, bucket);
    const bucketData = paymentsByKey[key] || { revenue: 0, orders: 0 };
    return {
      date: bucketLabelForDate(bucketMode, bucket, 'Week '),
      dateLong: bucketLongLabelForDate(bucketMode, bucket),
      revenue: bucketData.revenue / 100,
      orders: bucketData.orders,
      avgOrderValue: bucketData.orders > 0 ? bucketData.revenue / bucketData.orders / 100 : 0,
      sessions: sessionsByKey[key] || 0
    };
  });
};

/**
 * Get revenue timeline data
 * @param {string|null} dateRange - 'week', 'month', '2026-02', or null for all time
 * @param {Array<string>} orgBoothIds - Optional array of booth IDs to filter by organization
 */
export const getRevenueTimeline = async (dateRange = 'month', orgBoothIds = null) => {
  const range = resolveDateRange(dateRange);
  const cacheKey = `revenue-timeline-${range.cacheKey}-${orgBoothIds ? orgBoothIds.join(',') : 'all'}`;

  return getCachedOrFetch(cacheKey, async () => {
    const now = new Date();

    // ALL TIME: exact, cheap daily_stats rollups. Any failure (missing index,
    // permissions) or an empty collection (backfill not yet run) falls back
    // to the capped doc path below — charts keep working exactly as before.
    if (range.isAllTime) {
      try {
        const statRows = await fetchAllTimeStatRows(orgBoothIds);
        if (statRows) {
          return computeRevenueTimelineFromDailyStats({ now, orgBoothIds, statRows });
        }
      } catch (error) {
        reportRollupFallback('revenue-timeline', error);
      }
    }

    const [paymentDocs, sessionDocs] = await Promise.all([
      fetchPaymentDocsInRange(range),
      fetchSessionDocsInRange(range)
    ]);

    return computeRevenueTimeline({ range, now, orgBoothIds, sessionDocs, paymentDocs });
  });
};

/**
 * Pure aggregation for getPackagePerformance. Exported for unit tests.
 * `sessionDocs` is only consulted when org scoping is active.
 */
export const computePackagePerformance = ({ orgBoothIds, paymentDocs, sessionDocs }) => {
  let effectivePaymentDocs = paymentDocs;
  if (orgBoothIds) {
    const filteredSessions = filterSessionsByBooths(sessionDocs || [], orgBoothIds);
    const sessionIds = getSessionIdsFromSessions(filteredSessions);
    effectivePaymentDocs = filterPaymentsBySessions(paymentDocs, sessionIds);
  }

  const packageStats = {};

  effectivePaymentDocs.forEach(doc => {
    const data = doc.data();
    if (!isCountedAsRevenue(data)) return;
    const packageType = data.package_type || 'unknown';

    if (!packageStats[packageType]) {
      packageStats[packageType] = {
        name: packageType,
        count: 0,
        revenue: 0,
        credits: 0
      };
    }

    packageStats[packageType].count++;
    packageStats[packageType].revenue += (data.amount || 0) / 100;
    packageStats[packageType].credits += data.credits || 0;
  });

  return Object.values(packageStats).sort((a, b) => b.revenue - a.revenue);
};

/**
 * Get package performance data
 * @param {number|null} days - Number of days or null for all time
 * @param {Array<string>} orgBoothIds - Optional array of booth IDs to filter by organization
 */
export const getPackagePerformance = async (dateRange = 'month', orgBoothIds = null) => {
  const range = resolveDateRange(dateRange);
  const cacheKey = `package-performance-${range.cacheKey}-${orgBoothIds ? orgBoothIds.join(',') : 'all'}`;

  return getCachedOrFetch(cacheKey, async () => {
    // Sessions are only needed to scope payments to an org's booths.
    const [paymentDocs, sessionDocs] = await Promise.all([
      fetchPaymentDocsInRange(range),
      orgBoothIds ? fetchSessionDocsInRange(range) : Promise.resolve(null)
    ]);

    return computePackagePerformance({ orgBoothIds, paymentDocs, sessionDocs });
  });
};

/**
 * Pure aggregation for getBoothLeaderboard. Exported for unit tests.
 */
export const computeBoothLeaderboard = ({ orgBoothIds, boothDocs, sessionDocs, paymentDocs, limit = 10 }) => {
  // Filter by organization
  const filteredBoothDocs = orgBoothIds
    ? boothDocs.filter(doc => orgBoothIds.includes(doc.id))
    : boothDocs;

  // Drop staff-initiated sessions, then filter by org booths (if any).
  const sessionsFiltered = filterSessionsByBooths(sessionDocs, orgBoothIds);

  const sessionIds = orgBoothIds ? getSessionIdsFromSessions(sessionsFiltered) : null;
  const paymentsFiltered = orgBoothIds
    ? filterPaymentsBySessions(paymentDocs, sessionIds)
    : paymentDocs;

  const boothStats = {};

  // Initialize booth stats
  filteredBoothDocs.forEach(doc => {
    boothStats[doc.id] = {
      id: doc.id,
      name: doc.data().name || 'Unnamed Booth',
      location: doc.data().location || 'Unknown',
      sessions: 0,
      photos: 0,
      revenue: 0,
      status: doc.data().status
    };
  });

  // Build session ID to booth ID mapping
  const sessionIdToBoothId = new Map();
  sessionsFiltered.forEach(doc => {
    const data = doc.data();
    const boothId = data.booth_id;

    if (boothId) {
      // Map both session doc ID and anonymous_session_id to booth
      sessionIdToBoothId.set(doc.id, boothId);
      if (data.anonymous_session_id) {
        sessionIdToBoothId.set(data.anonymous_session_id, boothId);
      }

      // Aggregate session stats
      if (boothStats[boothId]) {
        boothStats[boothId].sessions++;
        boothStats[boothId].photos += data.photos?.length || 0;
      }
    }
  });

  // Aggregate revenue data by mapping payments to booths via session IDs
  paymentsFiltered.forEach(doc => {
    const data = doc.data();
    if (!isCountedAsRevenue(data)) return;
    const sessionId = data.anonymous_session_id;

    if (sessionId) {
      const boothId = sessionIdToBoothId.get(sessionId);
      if (boothId && boothStats[boothId]) {
        boothStats[boothId].revenue += (data.amount || 0) / 100;
      }
    }
  });

  // Calculate averages and sort
  return Object.values(boothStats)
    .map(booth => ({
      ...booth,
      avgPhotos: booth.sessions > 0 ? (booth.photos / booth.sessions).toFixed(1) : 0
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, limit); // Top N booths by revenue (10 on the dashboard)
};

/**
 * Rollup-sourced twin of computeBoothLeaderboard for the ALL-TIME view.
 * Revenue is attributed via each payment's denormalized booth_id (stamped
 * forward + backfilled), so it is exact rather than capped-doc approximate.
 * Identical output shape. Exported for unit tests.
 */
export const computeBoothLeaderboardFromDailyStats = ({ orgBoothIds, boothDocs, statRows, limit = 10 }) => {
  const filteredBoothDocs = orgBoothIds
    ? boothDocs.filter(doc => orgBoothIds.includes(doc.id))
    : boothDocs;

  const boothStats = {};
  filteredBoothDocs.forEach(doc => {
    boothStats[doc.id] = {
      id: doc.id,
      name: doc.data().name || 'Unnamed Booth',
      location: doc.data().location || 'Unknown',
      sessions: 0,
      photos: 0,
      revenue: 0,
      status: doc.data().status
    };
  });

  filterStatRows(statRows, orgBoothIds).forEach((row) => {
    const stats = row.booth_id && boothStats[row.booth_id];
    if (!stats) return; // unattributed bucket / unknown booth
    stats.sessions += row.sessions || 0;
    stats.photos += row.photos || 0;
    stats.revenue += (row.revenue_cents || 0) / 100;
  });

  return Object.values(boothStats)
    .map(booth => ({
      ...booth,
      avgPhotos: booth.sessions > 0 ? (booth.photos / booth.sessions).toFixed(1) : 0
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, limit); // Top N booths by revenue (10 on the dashboard)
};

/**
 * Get booth performance leaderboard
 * @param {number|null} days - Number of days or null for all time
 * @param {Array<string>} orgBoothIds - Optional array of booth IDs to filter by organization
 * @param {number} limit - Max rows returned (default 10, the dashboard panel size)
 */
export const getBoothLeaderboard = async (dateRange = 'month', orgBoothIds = null, limit = 10) => {
  const range = resolveDateRange(dateRange);
  const cacheKey = `booth-leaderboard-top${limit}-${range.cacheKey}-${orgBoothIds ? orgBoothIds.join(',') : 'all'}`;

  return getCachedOrFetch(cacheKey, async () => {
    // ALL TIME: exact daily_stats rollups, falling back to the capped doc
    // path on any failure or before the backfill has run.
    if (range.isAllTime) {
      try {
        const [boothDocs, statRows] = await Promise.all([
          fetchBoothDocs(),
          fetchAllTimeStatRows(orgBoothIds)
        ]);
        if (statRows) {
          return computeBoothLeaderboardFromDailyStats({ orgBoothIds, boothDocs, statRows, limit });
        }
      } catch (error) {
        reportRollupFallback('booth-leaderboard', error);
      }
    }

    const [boothDocs, sessionDocs, paymentDocs] = await Promise.all([
      fetchBoothDocs(),
      fetchSessionDocsInRange(range),
      fetchPaymentDocsInRange(range)
    ]);

    return computeBoothLeaderboard({ orgBoothIds, boothDocs, sessionDocs, paymentDocs, limit });
  });
};

/**
 * Pure aggregation for getRevenueByBoothTimeline. Exported for unit tests.
 */
export const computeRevenueByBoothTimeline = ({ range, now, orgBoothIds, boothDocs, sessionDocs, paymentDocs }) => {
  const { start: startDate, end: endDate, isAllTime } = range;

  const filteredBoothDocs = orgBoothIds
    ? boothDocs.filter((d) => orgBoothIds.includes(d.id))
    : boothDocs;

  // Drop staff-initiated sessions, then filter by org booths (if any).
  const filteredSessionDocs = filterSessionsByBooths(sessionDocs, orgBoothIds);

  const sessionIds = orgBoothIds ? getSessionIdsFromSessions(filteredSessionDocs) : null;
  const filteredPaymentDocs = orgBoothIds
    ? filterPaymentsBySessions(paymentDocs, sessionIds)
    : paymentDocs;

  // Map sessionId → boothId
  const sessionIdToBoothId = new Map();
  filteredSessionDocs.forEach((doc) => {
    const data = doc.data();
    const boothId = data.booth_id;
    if (!boothId) return;
    sessionIdToBoothId.set(doc.id, boothId);
    if (data.anonymous_session_id) {
      sessionIdToBoothId.set(data.anonymous_session_id, boothId);
    }
  });

  // Determine effective start for All Time from actual data
  let effectiveStartDate = startDate;
  if (isAllTime) {
    effectiveStartDate = now;
    filteredPaymentDocs.forEach((doc) => {
      const created = doc.data().created_at?.toDate();
      if (created && created < effectiveStartDate) effectiveStartDate = created;
    });
    filteredSessionDocs.forEach((doc) => {
      const ts = doc.data().timestamp?.toDate();
      if (ts && ts < effectiveStartDate) effectiveStartDate = ts;
    });
    effectiveStartDate = startOfDay(effectiveStartDate);
  }

  const chartEnd = endDate < now ? endDate : now;
  const bucketMode = bucketModeFor(effectiveStartDate, chartEnd);
  const getBucketKey = (date) => bucketKeyForDate(bucketMode, date);

  const bucketDates = bucketDatesFor(bucketMode, effectiveStartDate, chartEnd);
  const buckets = bucketDates.map((d) => ({
    key: getBucketKey(d),
    label: bucketLabelForDate(bucketMode, d),
    longLabel: bucketLongLabelForDate(bucketMode, d)
  }));
  const bucketIndex = new Map(buckets.map((b, i) => [b.key, i]));

  // Initialize per-booth series
  const boothStats = {};
  filteredBoothDocs.forEach((doc) => {
    boothStats[doc.id] = {
      id: doc.id,
      name: doc.data().name || 'Unnamed Booth',
      location: doc.data().location || '',
      total: 0,
      series: buckets.map((b) => ({ key: b.key, label: b.longLabel, revenue: 0 })),
    };
  });

  // Aggregate payments per booth × bucket
  filteredPaymentDocs.forEach((doc) => {
    const data = doc.data();
    if (!isCountedAsRevenue(data)) return;
    const sessionId = data.anonymous_session_id;
    if (!sessionId) return;
    const boothId = sessionIdToBoothId.get(sessionId);
    if (!boothId || !boothStats[boothId]) return;
    const created = data.created_at?.toDate();
    if (!created) return;
    const key = getBucketKey(created);
    const idx = bucketIndex.get(key);
    if (idx === undefined) return;
    const dollars = (data.amount || 0) / 100;
    boothStats[boothId].series[idx].revenue += dollars;
    boothStats[boothId].total += dollars;
  });

  const booths = Object.values(boothStats)
    .filter((b) => b.total > 0)
    .sort((a, b) => b.total - a.total);

  return { buckets, booths, bucketMode };
};

/**
 * Rollup-sourced twin of computeRevenueByBoothTimeline for the ALL-TIME view.
 * Per-booth daily_stats rows make the booth × bucket matrix exact without any
 * session-linkage joins. Identical output shape. Exported for unit tests.
 */
export const computeRevenueByBoothTimelineFromDailyStats = ({ now, orgBoothIds, boothDocs, statRows }) => {
  const filteredBoothDocs = orgBoothIds
    ? boothDocs.filter((d) => orgBoothIds.includes(d.id))
    : boothDocs;

  const rows = filterStatRows(statRows, orgBoothIds);
  if (rows.length === 0) return { buckets: [], booths: [], bucketMode: 'daily' };

  let effectiveStartDate = now;
  // The chart must end where the DATA ends, not at local `now`.
  //
  // Rollup rows and the live tail are both keyed by UTC day, while
  // parseDailyDate and the bucket list are local. West of UTC the newest UTC
  // day is tomorrow locally, so from 8 PM ET until midnight the live tail's
  // last row fell outside start..now and its revenue silently vanished from
  // the all-time charts — peak booth hours, and exactly the hours commit
  // a4d683f widened the FETCH to cover without aligning the buckets to match.
  let latestRowDate = null;
  rows.forEach((row) => {
    const day = parseDailyDate(row.date);
    if (day < effectiveStartDate) effectiveStartDate = day;
    if (!latestRowDate || day > latestRowDate) latestRowDate = day;
  });
  effectiveStartDate = startOfDay(effectiveStartDate);
  const chartEnd = latestRowDate && latestRowDate > now ? latestRowDate : now;

  const bucketMode = bucketModeFor(effectiveStartDate, chartEnd);
  const bucketDates = bucketDatesFor(bucketMode, effectiveStartDate, chartEnd);
  const buckets = bucketDates.map((d) => ({
    key: bucketKeyForDate(bucketMode, d),
    label: bucketLabelForDate(bucketMode, d),
    longLabel: bucketLongLabelForDate(bucketMode, d)
  }));
  const bucketIndex = new Map(buckets.map((b, i) => [b.key, i]));

  const boothStats = {};
  filteredBoothDocs.forEach((doc) => {
    boothStats[doc.id] = {
      id: doc.id,
      name: doc.data().name || 'Unnamed Booth',
      location: doc.data().location || '',
      total: 0,
      series: buckets.map((b) => ({ key: b.key, label: b.longLabel, revenue: 0 })),
    };
  });

  rows.forEach((row) => {
    const stats = row.booth_id && boothStats[row.booth_id];
    if (!stats) return; // unattributed bucket / unknown booth
    const idx = bucketIndex.get(bucketKeyForDate(bucketMode, parseDailyDate(row.date)));
    if (idx === undefined) return;
    const dollars = (row.revenue_cents || 0) / 100;
    stats.series[idx].revenue += dollars;
    stats.total += dollars;
  });

  const booths = Object.values(boothStats)
    .filter((b) => b.total > 0)
    .sort((a, b) => b.total - a.total);

  return { buckets, booths, bucketMode };
};

/**
 * Get per-booth revenue broken into daily/weekly buckets for the date range.
 * Returns: {
 *   buckets: [{ key, label }],
 *   booths: [{ id, name, location, total, series: [{ key, revenue }] }],
 * }
 * Bucketing matches getRevenueTimeline (hourly/daily/weekly/monthly).
 */
export const getRevenueByBoothTimeline = async (dateRange = 'month', orgBoothIds = null) => {
  const range = resolveDateRange(dateRange);
  const cacheKey = `revenue-by-booth-${range.cacheKey}-${orgBoothIds ? orgBoothIds.join(',') : 'all'}`;

  return getCachedOrFetch(cacheKey, async () => {
    const now = new Date();

    // ALL TIME: exact daily_stats rollups, falling back to the capped doc
    // path on any failure or before the backfill has run.
    if (range.isAllTime) {
      try {
        const [boothDocs, statRows] = await Promise.all([
          fetchBoothDocs(),
          fetchAllTimeStatRows(orgBoothIds)
        ]);
        if (statRows) {
          return computeRevenueByBoothTimelineFromDailyStats({ now, orgBoothIds, boothDocs, statRows });
        }
      } catch (error) {
        reportRollupFallback('revenue-by-booth', error);
      }
    }

    const [boothDocs, sessionDocs, paymentDocs] = await Promise.all([
      fetchBoothDocs(),
      fetchSessionDocsInRange(range),
      fetchPaymentDocsInRange(range),
    ]);

    return computeRevenueByBoothTimeline({ range, now, orgBoothIds, boothDocs, sessionDocs, paymentDocs });
  });
};

export default {
  getExecutiveKPIs,
  getRevenueTimeline,
  getRevenueByBoothTimeline,
  getPackagePerformance,
  getBoothLeaderboard,
  clearCache,
  clearDashboardCache
};
